import type { FraisDTO, StagiaireDTO } from '@/features/api/extranet/dto/Demande/DR'
import { DEMANDE_STORE } from '@/features/interface/demande/shared/components/demande-content/DemandeContent.types'
import { isNumber } from '@/features/shared/tools'
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'
import { useAjouteFrais } from '../../ajouter-frais/useAjouteFrais.ts'

export type Frais = FraisDTO & StagiaireDTO & { index: number; montantTTC?: number | null }
export function useModuleFrais(moduleIndex: number) {
  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_DR'>()
  const { model, meta, readonly, lov } = storeToRefs(store)
  const { setupArrayUpdaterWithKey, setupCustomUpdater } = store
  const module = computed(() => model.value!.modules![moduleIndex]!)
  const metaModule = computed(() => meta.value!.modules![moduleIndex]!)

  const frais = computed<Frais[]>(() => {
    // Extraction des frais du module et des stagiaires
    const result = model
      .value!.frais!.map((frais, index) => ({ ...frais, index }))
      .filter((frais) => frais.idModule === module.value.idModule)
      .map((frais) => {
        const stagiaire = model.value!.stagiaires!.find((s) => s.idIndividuAdhEtablissement === frais.idSalarie)!
        return {
          ...frais,
          ...stagiaire,
        }
      })
      .map((frais) => {
        if (isNumber(frais.montantDemandeRemboursement) && isNumber(frais.tauxTva)) {
          // Réalise le calcul sur des entiers puis le passe en flottant
          // pour limiter les écarts de calculs sur les flottants
          const montantHT = Math.round(frais.montantDemandeRemboursement * 100)
          const tauxTVA = Math.round(frais.tauxTva * 1000) + 1000
          const montantTTC = Math.round(montantHT * tauxTVA) / (100 * 1000)
          return {
            ...frais,
            montantTTC,
          }
        }
        return { ...frais, montantTTC: null }
      })

    // Trie sur la nature de frais
    result.sort((a, b) => a.natureFrais!.localeCompare(b.natureFrais!))
    // Trie sur la nom prenom
    result.sort((a, b) => `${a.nom} ${a.prenom}`.toLowerCase().localeCompare(`${b.nom} ${b.prenom}`.toLowerCase()))
    return result
  })

  const updateFrais = setupArrayUpdaterWithKey<FraisDTO>('frais')

  const deleteFrais = setupCustomUpdater<FraisDTO>((patch, frais) => {
    const index = patch.frais.findIndex(
      (f) =>
        f.idModule === frais.idModule &&
        f.idSalarie === frais.idSalarie &&
        f.natureFrais === frais.natureFrais &&
        f.estSupprimable === true,
    )
    patch.frais?.splice(index, 1)
  })

  const patchAddFraisSupplementaires = setupCustomUpdater<FraisDTO[]>((patch, frais) => {
    patch.frais.push(...frais)
  })

  const ajouteFraisDialog = useAjouteFrais()
  const addFraisSupplementaires = async () => {
    const result = await ajouteFraisDialog.showDialog({
      idModule: module.value.idModule!,
      dispositif: model.value!.dispositif!,
      sousDispositif: model.value!.sousDispositif,
      tauxTVA: model.value!.tauxTva,
      frais: frais.value!,
      stagiaires: model.value!.stagiaires!,
      lovNatureFrais: lov.value.NatureFrais,
    })

    if (result.statut === 'OK') {
      if (result.frais.length > 0) patchAddFraisSupplementaires(result.frais)
    } else {
      // Todo: A retirer
      console.error(result.error)
    }
  }

  return {
    frais,
    meta,
    metaModule,
    model,
    module,
    readonly,
    updateFrais,
    deleteFrais,
    addFraisSupplementaires,
    lov,
  }
}
